import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { GOOGLE_CONFIG } from '../config/auth.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const AUTHORIZED_EMAIL = GOOGLE_CONFIG.AUTHORIZED_EMAIL;
  
  // Check if we're on localhost (development mode)
  const isLocalhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';

  const checkAuthStatus = useCallback(() => {
    // Skip OAuth for localhost (development mode)
    if (isLocalhost) {
      setUser({
        email: AUTHORIZED_EMAIL,
        name: 'Developer',
        picture: '',
        sub: 'localhost-dev'
      });
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }

    // Production mode - check OAuth session
    const savedUser = localStorage.getItem('admin_user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        if (userData.email === AUTHORIZED_EMAIL) {
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('admin_user');
        }
      } catch (error) {
        localStorage.removeItem('admin_user');
      }
    }
    setLoading(false);
  }, [AUTHORIZED_EMAIL, isLocalhost]);

  const handleCredentialResponse = useCallback((response) => {
    // Decode the JWT token
    const token = response.credential;
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // Check if this is your authorized email
    if (payload.email === AUTHORIZED_EMAIL) {
      const userData = {
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
        sub: payload.sub
      };
      
      setUser(userData);
      setIsAuthenticated(true);
      localStorage.setItem('admin_user', JSON.stringify(userData));
    } else {
      alert('Access denied. Only authorized users can access the admin panel.');
    }
  }, [AUTHORIZED_EMAIL]);

  useEffect(() => {
    // Skip OAuth setup for localhost (development mode)
    if (isLocalhost) {
      console.log('Development mode: Skipping OAuth setup');
      checkAuthStatus();
      return;
    }

    // Production mode - Initialize Google OAuth
    const initializeGoogleAuth = () => {
      if (window.google) {
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CONFIG.CLIENT_ID,
            callback: handleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true
          });
          console.log('Google OAuth initialized successfully');
        } catch (error) {
          console.error('Failed to initialize Google OAuth:', error);
        }
      } else {
        console.error('Google OAuth script not loaded');
      }
    };

    // Load Google OAuth script
    const loadGoogleScript = () => {
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = initializeGoogleAuth;
        script.onerror = () => {
          console.error('Failed to load Google OAuth script. Please check your internet connection and try again.');
        };
        document.head.appendChild(script);
      } else {
        initializeGoogleAuth();
      }
    };

    loadGoogleScript();
    
    // Check for existing session
    checkAuthStatus();
  }, [checkAuthStatus, handleCredentialResponse, isLocalhost]);

  const signIn = () => {
    // Skip OAuth for localhost (development mode)
    if (isLocalhost) {
      console.log('Development mode: Auto-signing in');
      setUser({
        email: AUTHORIZED_EMAIL,
        name: 'Developer',
        picture: '',
        sub: 'localhost-dev'
      });
      setIsAuthenticated(true);
      return;
    }

    // Production mode - use Google OAuth
    if (window.google) {
      try {
        window.google.accounts.id.prompt();
      } catch (error) {
        console.error('Error during sign-in:', error);
        alert('Sign-in failed. Please check the console for details and ensure your domain is authorized in Google Cloud Console.');
      }
    } else {
      console.error('Google OAuth not loaded');
      alert('Google OAuth not loaded. Please refresh the page and try again.');
    }
  };

  const signOut = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('admin_user');
    
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    signIn,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
